'use client'

import {
  CaretLeftIcon,
  CaretRightIcon,
  ChatCircleIcon,
  MapPinIcon,
  StarIcon,
  UserCircleCheckIcon,
} from '@phosphor-icons/react'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'
import { FahrlehrerCh } from '@/src/@types/blinkadmin'
import Button from '@/src/components/core/Button'
import Picture from '@/src/components/elements/Picture'
import Container from '@/src/components/layout/Container'

function getAverageRating(
  reviews: FahrlehrerCh.TeacherProfileReview[]
): number | null {
  const values: number[] = []
  for (const review of reviews) {
    const r = review.ratings
    for (const v of [
      r.progress,
      r.explanations,
      r.learningGoals,
      r.attention,
      r.patience,
      r.scheduling,
      r.cleanliness,
      r.punctuality,
    ]) {
      if (v !== null) values.push(v)
    }
  }
  return values.length > 0
    ? values.reduce((s, v) => s + v, 0) / values.length
    : null
}

function fmtRating(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

const InstructorCard = ({
  instructor,
  selectedLocationId,
}: {
  instructor: FahrlehrerCh.TeacherProfileOtherInstructor
  selectedLocationId: number
}) => {
  const yearsExperience = instructor.certificationSince
    ? new Date().getFullYear() - instructor.certificationSince
    : null

  const otherLocations = instructor.locations.filter(
    (l) => l.id !== selectedLocationId
  )
  const sortedLocations = [
    ...instructor.locations.filter((l) => l.id === selectedLocationId),
    ...otherLocations.filter((l) => l.hasBlinkLandingpage),
    ...otherLocations.filter((l) => !l.hasBlinkLandingpage),
  ]
  const displayedLocations = sortedLocations.slice(0, 2)
  const overflowCount = sortedLocations.length - displayedLocations.length
  const locationsText =
    displayedLocations.map((l) => l.name).join(', ') +
    (overflowCount > 0 ? ` + ${overflowCount}` : '')

  const avgRating = getAverageRating(instructor.reviews)

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-150 bg-gray-50">
      <div className="flex items-center gap-4 px-5 pt-5 pb-4">
        <div className="relative size-14 shrink-0 overflow-hidden rounded-full ring-2 ring-gray-100">
          {instructor.avatar && (
            <Picture
              permalink={instructor.avatar.url}
              alt={instructor.name}
              fill
              className="object-cover"
              sizes="200px"
            />
          )}
        </div>
        <div className="min-w-0">
          <p className="truncate text-lg font-black">{instructor.name}</p>
          {instructor.shortDescription && (
            <p className="text-sm text-gray-500">
              {instructor.shortDescription}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col justify-between gap-6 px-5 pt-2 pb-5">
        <div className="space-y-2">
          {displayedLocations.length > 0 && (
            <div className="flex items-start gap-2 text-sm text-gray-600">
              <MapPinIcon size={20} weight="bold" className="mt-px shrink-0" />
              <span>{locationsText}</span>
            </div>
          )}
          {instructor.lessonLanguages.length > 0 && (
            <div className="flex items-start gap-2 text-sm text-gray-600">
              <ChatCircleIcon
                size={20}
                weight="bold"
                className="mt-px shrink-0"
              />
              <span>{instructor.lessonLanguages.join(', ')}</span>
            </div>
          )}
          {yearsExperience !== null && (
            <div className="flex items-start gap-2 text-sm text-gray-600">
              <UserCircleCheckIcon
                size={20}
                weight="bold"
                className="mt-px shrink-0"
              />
              <span>{yearsExperience} Jahre Erfahrung</span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-6">
          {avgRating !== null && (
            <div className="flex shrink-0 flex-col">
              <div className="flex items-center gap-1">
                <span className="text-sm font-bold">
                  {fmtRating(avgRating)}/5
                </span>
                <StarIcon size={16} weight="fill" className="text-yellow-500" />
              </div>
              <span className="text-xs text-gray-500">
                {instructor.reviews.length}{' '}
                {instructor.reviews.length === 1 ? 'Bewertung' : 'Bewertungen'}
              </span>
            </div>
          )}
          {avgRating === null && <div />}
          <Button
            as={Link}
            href={`/${instructor.handle}`}
            version="gray-outlined"
            size="small"
            className={avgRating === null ? 'w-full' : undefined}
          >
            Profil anzeigen
          </Button>
        </div>
      </div>
    </div>
  )
}

const MoreInstructors = ({
  otherInstructors,
  selectedLocation,
}: FahrlehrerCh.TeacherProfile & {
  selectedLocation: FahrlehrerCh.TeacherProfileLocation
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    breakpoints: {
      '(min-width: 768px)': { skipSnaps: true },
    },
  })
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on('select', onSelect).on('reInit', onSelect)
  }, [emblaApi, onSelect])

  const instructorsAtLocation = otherInstructors.filter((instructor) =>
    instructor.locations.some((l) => l.id === selectedLocation.id)
  )

  if (instructorsAtLocation.length === 0) return null

  return (
    <section className="mt-20 pb-20 md:mt-24 md:pb-24">
      <Container>
        <h2 className="text-4xl font-black md:text-10xl">
          Weitere Fahrlehrer:innen in {selectedLocation.name}
        </h2>
      </Container>

      <div ref={emblaRef} className="mt-6 overflow-hidden pb-10">
        <div className="flex items-stretch select-none">
          {instructorsAtLocation.map((instructor, i) => (
            <div
              key={i}
              className="ml-5 min-w-0 flex-[0_0_calc(100%-40px)] last:mr-5 min-[1342px]:first:ml-[calc((100%-1280px)/2)] min-[1342px]:last:mr-[calc((100%-1280px)/2)]! md:ml-6 md:flex-[0_0_304px] md:last:mr-6"
            >
              <InstructorCard
                instructor={instructor}
                selectedLocationId={selectedLocation.id}
              />
            </div>
          ))}
        </div>
      </div>

      <Container>
        <div className="flex justify-end gap-3">
          <Button
            icon={<CaretLeftIcon weight="bold" />}
            size="icon-small"
            version="gray-outlined"
            onClick={() => emblaApi?.scrollPrev()}
            disabled={!canScrollPrev}
            aria-label="Vorheriger Fahrlehrer"
          />
          <Button
            icon={<CaretRightIcon weight="bold" />}
            size="icon-small"
            version="gray-outlined"
            onClick={() => emblaApi?.scrollNext()}
            disabled={!canScrollNext}
            aria-label="Nächster Fahrlehrer"
          />
        </div>
      </Container>
    </section>
  )
}

export default MoreInstructors
